
import { defineComponent, computed, ref, reactive, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import DssIcon from '../atoms/DssIcon.vue'
import TheGlobalNav from './TheGlobalNav.vue'
import TheMyCollectionHeader from './TheMyCollectionHeader.vue'
import TheRekionAdminMenuHeader from './TheRekionAdminMenuHeader.vue'
import LogoRekion from '@/components/organisms/rekion/LogoRekion.vue'
import LogoKn from '@/components/organisms/kn/LogoKn.vue'
import LogoNdldc from '@/components/organisms/LogoNdldc.vue'
import md from '@/helpers/util/MobileDetect'
import ModalWindow from '@/components/organisms/ModalWindow.vue'
import TheLoginForm from '@/components/organisms/TheLoginForm.vue'

export default defineComponent({
  name: 'TheGlobalHeader',
  components: {
    TheGlobalNav,
    TheMyCollectionHeader,
    TheRekionAdminMenuHeader,
    DssIcon,
    LogoRekion,
    LogoKn,
    LogoNdldc,
    ModalWindow,
    TheLoginForm,
  },
  props: {
    isRekion: {
      type: Boolean,
      default: false
    },
    isKn: {
      type: Boolean,
      default: false
    },
    headerType: {
      type: String,
      default: 'underlayer'
    }
  },
  setup (props) {
    const KN_APPLICATION_LINK = process.env.VUE_APP_KN_APPLICATION_LINK

    const breakpoint = md.width()
    const route = useRoute()
    const store = useStore()

    const i18n = useI18n()
    const lang = i18n.locale

    const path = computed(() => route.path)
    const isLoggedIn = computed(() => store.getters.isLoggedIn)
    const loggedInUser = computed(() => store.getters.loggedInUser)

    // myCollectionページ = true
    const isMyCollectionPage = computed(() => {
      return route.name === 'MyCollection' && isLoggedIn.value
    })

    // れきおん管理者メニューページ = true
    const isRekionAdminMenu = computed(() => {
      return route.name === 'AdminMenu' && loggedInUser.value.role?.includes('REKION_ADMIN')
    })

    const show = ref(false)
    const logoType = computed(() => {
      if (breakpoint.value && (props.headerType !== 'top-page')) {
        return 'underlayer'
      }
      return props.headerType
    })

    const headerStyle = computed(() => {
      return {
        'header-top-page': logoType.value === 'top-page',
        'header-underlayer': logoType.value === 'underlayer',
        'header-mycollection-page': isMyCollectionPage.value,
        'has-background-contents': !props.isRekion && !props.isKn,
        'has-background-rekion-main': props.isRekion,
        'has-background-kn-main': props.isKn,
      }
    })

    const nav = ref<HTMLDivElement>()
    const header = ref<HTMLDivElement>()
    const heightDuringAnimation = reactive<{ parent: null | number, nav: null | number }>({
      parent: null,
      nav: null
    })
    const duringAnimation = ref(false)
    const toggleHeight = (isEnter: boolean) => {
      if (
        header.value &&
        nav.value &&
        breakpoint.value
      ) {
        if (isEnter) {
          const navHeight = (nav.value.children[0] as HTMLElement).offsetHeight
          heightDuringAnimation.parent = header.value.clientHeight + navHeight
          heightDuringAnimation.nav = navHeight
        } else {
          heightDuringAnimation.parent = null
          heightDuringAnimation.nav = null
        }
      }
    }

    /**
     * アニメーションに待機時間を設定する
     * @param element 対象のHTML要素の配列
     * @param standby 最初のアニメーションが発火するまでの待機時間 (秒)
     * @param delay 次のアニメーションが発火するまでの待機時間 (秒)
     * @param isEnter アニメーションの状態
     */
    const setAnimationDelaySec = (params: {
      element: HTMLElement[],
      standby: number,
      delay: number
      isEnter: boolean
    }) => {
      const delay = params.delay
      params.element && params.element.forEach((h, i) => {
        if (!params.isEnter) return ((h as HTMLElement).style.transitionDelay = '')
        const sec_ = (params.standby || 0) + delay * i
        ;(h as HTMLElement).style.transitionDelay = `${sec_}s`
      })
    }

    const toggle = async () => {
      if (!breakpoint.value) return
      show.value = !show.value
      duringAnimation.value = true
      setTimeout(() => (duringAnimation.value = false), 500)
    }

    watch(() => show.value, (newValue_) => {
      setTimeout(() => toggleHeight(newValue_), 10) // v-show の display: none が切り替わってから開閉する
    })
    watch(() => duringAnimation.value, (newValue_) => {
      setAnimationDelaySec({
        element: Array.from(window.document.querySelectorAll<HTMLElement>('.has-animation')),
        standby: 0,
        delay: 0.05,
        isEnter: newValue_
      })
    })

    const headerStyleDuringAnimation = computed(() => ({
      height: heightDuringAnimation.parent && `${heightDuringAnimation.parent}px`,
      ...duringAnimation.value && {
        transitionDuration: '0.2s',
        transitionProperty: 'height',
        transitionTimingFunction: 'ease-out'
      }
    }))

    /**
     * グローバルナビの開閉ステータス、高さをリセットする処理
     */
    const resetNavStyle = () => {
      show.value = false
      heightDuringAnimation.parent = null
      heightDuringAnimation.nav = null
    }
    // ページ遷移時、グローバルナビをリセット
    watch(() => path.value, resetNavStyle)

    return {
      KN_APPLICATION_LINK,
      headerStyle,
      show,
      logoType,
      isMyCollectionPage,
      isRekionAdminMenu,
      path,
      toggle,
      header,
      nav,
      heightDuringAnimation,
      headerStyleDuringAnimation,
      breakpoint,
      lang
    }
  }
})
